export const RESEND_LOCK_MS = 15 * 60 * 1000;
export const RESEND_LOCK_STORAGE_KEY = "btForgotPasswordResendLock";
export const RESEND_COOLDOWN_STORAGE_KEY = "btForgotPasswordResendCooldown";
export const FAMILY_EMAIL_VERIFY_RESEND_LOCK_KEY = "btFamilyEmailVerifyResendLock";
export const FAMILY_MOBILE_VERIFY_RESEND_LOCK_KEY = "btFamilyMobileVerifyResendLock";

export const normalizeForgotPasswordEmail = (email) =>
  String(email || "")
    .trim()
    .toLowerCase();

export const parseLockedUntilMs = (lockedUntil) => {
  if (lockedUntil == null || lockedUntil === "") {
    return null;
  }

  if (typeof lockedUntil === "number" && Number.isFinite(lockedUntil)) {
    return lockedUntil < 1e12 ? lockedUntil * 1000 : lockedUntil;
  }

  const numericValue = Number(lockedUntil);
  if (Number.isFinite(numericValue)) {
    return numericValue < 1e12 ? numericValue * 1000 : numericValue;
  }

  const timestamp = new Date(lockedUntil).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const isSendOtpRateLimitError = (data) => {
  if (!data) {
    return false;
  }

  const lockedUntilMs = parseLockedUntilMs(data.lockedUntil);
  if (lockedUntilMs && lockedUntilMs > Date.now()) {
    return true;
  }

  if (data.attemptsRemaining === 0 && (lockedUntilMs || typeof data.minutesRemaining === "number")) {
    return true;
  }

  if (typeof data.minutesRemaining === "number" && data.minutesRemaining > 0) {
    return true;
  }

  return false;
};

export const parseWaitSecondsFromMessage = (message = "") => {
  const match = String(message).match(/wait\s+(\d+)\s+seconds?/i);
  if (!match) {
    return null;
  }

  const seconds = Number(match[1]);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
};

export const isSendOtpTooSoonError = (data, message = "") => {
  if (isSendOtpRateLimitError(data)) {
    return false;
  }

  const nextRequestMs = parseLockedUntilMs(data?.nextRequestAfter);
  if (nextRequestMs != null && nextRequestMs > Date.now()) {
    return true;
  }

  return parseWaitSecondsFromMessage(message) != null;
};

export const resolveResendCooldownEndsAt = (nextRequestAfter, message = "", minRequestIntervalSeconds = 0) => {
  const nextRequestMs = parseLockedUntilMs(nextRequestAfter);
  if (nextRequestMs && nextRequestMs > Date.now()) {
    return nextRequestMs;
  }

  const waitSeconds = parseWaitSecondsFromMessage(message);
  if (waitSeconds != null) {
    return Date.now() + waitSeconds * 1000;
  }

  const intervalSeconds = Number(minRequestIntervalSeconds);
  if (Number.isFinite(intervalSeconds) && intervalSeconds > 0) {
    return Date.now() + intervalSeconds * 1000;
  }

  return null;
};

export const getCooldownRemainingSeconds = (cooldownEndsAt) => {
  if (!cooldownEndsAt) {
    return 0;
  }
  return Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000));
};

export const isOtpWaitMessage = (message = "") => parseWaitSecondsFromMessage(message) != null;

export const formatOtpCooldownMessage = (totalSeconds) => {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return `Please wait ${minutes}:${String(remainder).padStart(2, "0")} before requesting another code.`;
  }
  return `Please wait ${seconds} seconds before requesting another code.`;
};

export const formatResendLockDuration = (totalSeconds) => {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} hr ${minutes} min`;
  }
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return `${minutes} min ${remainder} sec`;
  }
  return `${seconds} sec`;
};

export const formatResendLockError = (totalSeconds) => `Too many OTP requests. Please try again in ${formatResendLockDuration(totalSeconds)}.`;

export const formatMobileSendLockMessage = (totalSeconds) => {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  if (!seconds) {
    return "Reached maximum attempts. Please try again later.";
  }
  return `Reached maximum attempts. Please try after ${formatResendLockDuration(seconds)}.`;
};

const normalizeMobileLockKey = (mobileNumber) =>
  String(mobileNumber || "")
    .replace(/\D/g, "")
    .slice(-10);

export const readPersistedMobileResendLock = (mobileNumber) => {
  const mobileKey = normalizeMobileLockKey(mobileNumber);
  if (!mobileKey) {
    return null;
  }

  try {
    const raw = localStorage.getItem(FAMILY_MOBILE_VERIFY_RESEND_LOCK_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (parsed?.mobileKey !== mobileKey || !parsed?.lockedUntil) {
      return null;
    }

    const lockedUntilMs = normalizeLockTimestampMs(parsed.lockedUntil);
    if (!lockedUntilMs || lockedUntilMs <= Date.now()) {
      localStorage.removeItem(FAMILY_MOBILE_VERIFY_RESEND_LOCK_KEY);
      return null;
    }

    return lockedUntilMs;
  } catch {
    return null;
  }
};

export const writePersistedMobileResendLock = (mobileNumber, lockedUntilMs) => {
  const mobileKey = normalizeMobileLockKey(mobileNumber);
  if (!mobileKey || !lockedUntilMs) {
    return;
  }

  localStorage.setItem(FAMILY_MOBILE_VERIFY_RESEND_LOCK_KEY, JSON.stringify({ mobileKey, lockedUntil: lockedUntilMs }));
};

export const clearPersistedMobileResendLock = () => {
  localStorage.removeItem(FAMILY_MOBILE_VERIFY_RESEND_LOCK_KEY);
};

export const readPersistedEmailVerifyResendLock = (email) => {
  const normalizedEmail = normalizeForgotPasswordEmail(email);
  if (!normalizedEmail) {
    return null;
  }

  try {
    const raw = localStorage.getItem(FAMILY_EMAIL_VERIFY_RESEND_LOCK_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (parsed?.email !== normalizedEmail || !parsed?.lockedUntil) {
      return null;
    }

    const lockedUntilMs = normalizeLockTimestampMs(parsed.lockedUntil);
    if (!lockedUntilMs || lockedUntilMs <= Date.now()) {
      localStorage.removeItem(FAMILY_EMAIL_VERIFY_RESEND_LOCK_KEY);
      return null;
    }

    return lockedUntilMs;
  } catch {
    return null;
  }
};

export const writePersistedEmailVerifyResendLock = (email, lockedUntilMs) => {
  const normalizedEmail = normalizeForgotPasswordEmail(email);
  if (!normalizedEmail || !lockedUntilMs) {
    return;
  }

  localStorage.setItem(FAMILY_EMAIL_VERIFY_RESEND_LOCK_KEY, JSON.stringify({ email: normalizedEmail, lockedUntil: lockedUntilMs }));
};

export const clearPersistedEmailVerifyResendLock = () => {
  localStorage.removeItem(FAMILY_EMAIL_VERIFY_RESEND_LOCK_KEY);
};

export const resolveEmailVerifyLockEndsAt = ({ email, lockedUntil, attemptsRemaining }) => {
  const persisted = readPersistedEmailVerifyResendLock(email);
  if (persisted) {
    return persisted;
  }

  const serverLockMs = parseLockedUntilMs(lockedUntil);
  if (serverLockMs && serverLockMs > Date.now()) {
    return serverLockMs;
  }

  if (attemptsRemaining === 0) {
    return Date.now() + RESEND_LOCK_MS;
  }

  return null;
};

const normalizeLockTimestampMs = (value) => {
  if (value == null || value === "") {
    return null;
  }

  const numericValue = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return numericValue < 1e12 ? numericValue * 1000 : numericValue;
};

export const readPersistedResendLock = (email) => {
  const normalizedEmail = normalizeForgotPasswordEmail(email);
  if (!normalizedEmail) {
    return null;
  }

  try {
    const raw = localStorage.getItem(RESEND_LOCK_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (parsed?.email !== normalizedEmail || !parsed?.lockedUntil) {
      return null;
    }

    const lockedUntilMs = normalizeLockTimestampMs(parsed.lockedUntil);
    if (!lockedUntilMs || lockedUntilMs <= Date.now()) {
      localStorage.removeItem(RESEND_LOCK_STORAGE_KEY);
      return null;
    }

    return lockedUntilMs;
  } catch {
    return null;
  }
};

export const writePersistedResendLock = (email, lockedUntilMs) => {
  const normalizedEmail = normalizeForgotPasswordEmail(email);
  if (!normalizedEmail || !lockedUntilMs) {
    return;
  }

  localStorage.setItem(
    RESEND_LOCK_STORAGE_KEY,
    JSON.stringify({
      email: normalizedEmail,
      lockedUntil: lockedUntilMs,
    }),
  );
};

export const clearPersistedResendLock = () => {
  localStorage.removeItem(RESEND_LOCK_STORAGE_KEY);
};

export const readPersistedResendCooldown = (email) => {
  const normalizedEmail = normalizeForgotPasswordEmail(email);
  if (!normalizedEmail) {
    return null;
  }

  try {
    const raw = localStorage.getItem(RESEND_COOLDOWN_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (parsed?.email !== normalizedEmail || !parsed?.cooldownUntil) {
      return null;
    }

    const cooldownUntilMs = normalizeLockTimestampMs(parsed.cooldownUntil);
    if (!cooldownUntilMs || cooldownUntilMs <= Date.now()) {
      localStorage.removeItem(RESEND_COOLDOWN_STORAGE_KEY);
      return null;
    }

    return cooldownUntilMs;
  } catch {
    return null;
  }
};

export const writePersistedResendCooldown = (email, cooldownUntilMs) => {
  const normalizedEmail = normalizeForgotPasswordEmail(email);
  if (!normalizedEmail || !cooldownUntilMs) {
    return;
  }

  localStorage.setItem(
    RESEND_COOLDOWN_STORAGE_KEY,
    JSON.stringify({
      email: normalizedEmail,
      cooldownUntil: cooldownUntilMs,
    }),
  );
};

export const clearPersistedResendCooldown = () => {
  localStorage.removeItem(RESEND_COOLDOWN_STORAGE_KEY);
};

export const clearAllOtpResendLocks = () => {
  clearPersistedResendLock();
  clearPersistedResendCooldown();
  clearPersistedEmailVerifyResendLock();
  clearPersistedMobileResendLock();
};

export const resolveResendLockEndsAt = ({ email, lockedUntil, attemptsRemaining }) => {
  const persisted = readPersistedResendLock(email);
  if (persisted) {
    return persisted;
  }

  const serverLockMs = parseLockedUntilMs(lockedUntil);
  if (serverLockMs && serverLockMs > Date.now()) {
    return serverLockMs;
  }

  if (attemptsRemaining === 0) {
    return Date.now() + RESEND_LOCK_MS;
  }

  return null;
};
